import { round } from "lodash";

import { type DesignElementManager } from "@hooks/use-design-element-manager";
import { safeDivide } from "@north-beam/nb-common";
import { type BudgetMixBreakdownSpendDatum } from "@north-beam/nb-common";

import {
  type BudgetMixChartDatum,
  type BudgetMixChartData,
} from "./segmented-chart";

type BreakdownReturn = {
  budgetMixChartData: BudgetMixChartData;
  totalSpendForGroup: number;
  totalSpend: number;
};

export function breakdownDataBy(
  data: BudgetMixBreakdownSpendDatum[],
  designElementManager: DesignElementManager,
  breakdownIdentifier: string | undefined,
): BreakdownReturn {
  if (!breakdownIdentifier) {
    return {
      budgetMixChartData: [],
      totalSpendForGroup: 0,
      totalSpend: 0,
    };
  }

  let totalGroupSpend = 0;
  const reducer = (
    aggregate: Map<string, BudgetMixChartDatum>,
    datum: BudgetMixBreakdownSpendDatum,
  ) => {
    const valueToGroupBy = datum.breakdowns.find(
      (breakdown) => breakdown.key === breakdownIdentifier,
    )?.value;
    if (!valueToGroupBy) {
      throw new Error(
        `Failed to find breakdown ${breakdownIdentifier} in data with breakdowns ${JSON.stringify(
          datum.breakdowns,
        )}`,
      );
    }
    totalGroupSpend = round(totalGroupSpend + datum.spend, 2);
    const aggregatedDatum = aggregate.get(valueToGroupBy);

    const breakdownKey = datum.breakdowns?.[1]?.key ?? datum.breakdowns[0].key;
    const breakdownValue =
      datum.breakdowns?.[1]?.value ?? datum.breakdowns[0].value;

    if (aggregatedDatum) {
      aggregatedDatum.spend += datum.spend;
      aggregatedDatum.children.push({
        breakdownKey,
        breakdownValue,
        designElement: designElementManager.getDesignElement(valueToGroupBy),
        percentage: 0,
        spend: datum.spend,
        children: [],
      });
    } else {
      const newDatum: BudgetMixChartDatum = {
        breakdownKey: breakdownIdentifier,
        breakdownValue: valueToGroupBy,
        designElement: designElementManager.getDesignElement(valueToGroupBy),
        percentage: 0,
        spend: datum.spend,
        children: [
          {
            breakdownKey,
            breakdownValue,
            designElement:
              designElementManager.getDesignElement(valueToGroupBy),
            percentage: 0,
            spend: datum.spend,
            children: [],
          },
        ],
      };
      aggregate.set(valueToGroupBy, newDatum);
    }

    return aggregate;
  };

  const budgetMixChartData = Array.from(
    data.reduce(reducer, new Map()).values(),
  );
  budgetMixChartData.forEach((groupedDatum) => {
    groupedDatum.percentage = safeDivide(
      100 * groupedDatum.spend,
      totalGroupSpend,
      { zeroOverZero: 0, infOverInf: 1 },
    );
    groupedDatum.children.forEach((child) => {
      child.percentage = safeDivide(100 * child.spend, groupedDatum.spend, {
        zeroOverZero: 0,
        infOverInf: 1,
      });
    });
  });

  return {
    budgetMixChartData,
    totalSpendForGroup: totalGroupSpend,
    totalSpend: totalGroupSpend,
  };
}
